import React from 'react';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { cn } from '../lib/utils';
import { useAuthStore } from '../store';
import { useReactToMovie } from '../hooks/useMovie';
import { getApiErrorMessage } from '../api/errors';

const RATING_TO_REACTION = { down: 'Dislike', up: 'Like', 'double-up': 'DoubleLike' };
const REACTION_TO_RATING = { Dislike: 'down', Like: 'up', DoubleLike: 'double-up' };

export function RatingButton({ title, size = 'default', className }) {
  const token = useAuthStore((state) => state.token);
  const reactionMutation = useReactToMovie();
  const [open, setOpen] = React.useState(false);
  const closeTimer = React.useRef(null);

  const movieId = title?.id || title?.Id || title?.MovieId;
  const rating = REACTION_TO_RATING[title?.reactions?.CurrentUserReaction] || null;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openFlyout = () => {
    clearCloseTimer();
    setOpen(true);
  };

  const scheduleClose = () => {
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setOpen(false), 200);
  };

  React.useEffect(() => clearCloseTimer, []);

  const sizeClasses = {
    sm: 'size-8 text-xs',
    default: 'size-10 text-sm',
    lg: 'size-12 text-base',
  }[size] || 'size-10 text-sm';

  const iconSizes = {
    sm: 'size-3.5',
    default: 'size-4.5',
    lg: 'size-5',
  }[size] || 'size-4.5';

  const triggerIcon =
    rating === 'down'
      ? 'si:thumb-down-alt-1-fill'
      : rating === 'up' || rating === 'double-up'
        ? 'si:thumb-up-alt-1-fill'
        : 'si:thumb-up-alt-1-line';

  const select = (value) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    scheduleClose();
    if (!token) {
      toast.error('Sign in to react to titles');
      return;
    }
    if (!movieId) return;
    const reaction = rating === value ? 'None' : RATING_TO_REACTION[value];
    try {
      await reactionMutation.mutateAsync({ movieId: Number(movieId), reaction });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save reaction'));
    }
  };

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={openFlyout}
      onMouseLeave={scheduleClose}
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        onFocus={openFlyout}
        onBlur={scheduleClose}
        aria-label="Rate this title"
        aria-expanded={open}
        className={cn(
          'relative flex items-center justify-center rounded-full border border-white/25 bg-white/10 text-white backdrop-blur-md transition-all duration-200 hover:border-white/40 hover:bg-white/20 select-none cursor-pointer',
          sizeClasses,
          rating && 'border-primary/50 bg-primary/15 text-primary',
          className
        )}
      >
        <Icon icon={triggerIcon} className={iconSizes} />
      </button>

      <div
        role="menu"
        className={cn(
          'absolute left-1/2 bottom-full z-20 mb-2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-white/15 bg-[#16121f]/95 p-1 shadow-xl shadow-black/50 backdrop-blur-md transition-all duration-200 ease-out origin-bottom',
          open
            ? 'visible scale-100 opacity-100 translate-y-0'
            : 'invisible scale-95 opacity-0 translate-y-1 pointer-events-none'
        )}
      >
        <RatingOption
          active={rating === 'down'}
          label="Not for me"
          icon="si:thumb-down-alt-1-duotone"
          onClick={select('down')}
        />
        <RatingOption
          active={rating === 'up'}
          label="I like this"
          icon="si:thumb-up-alt-1-duotone"
          onClick={select('up')}
        />
        <DoubleThumbsOption
          active={rating === 'double-up'}
          onClick={select('double-up')}
        />
      </div>
    </div>
  );
}

function RatingOption({ active, label, icon, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className={cn(
        'flex size-9 items-center justify-center rounded-full text-white/80 transition-all duration-200 hover:scale-110 hover:bg-white/15 hover:text-white',
        active && 'bg-primary/20 text-primary'
      )}
    >
      <Icon icon={icon} className="size-4.5" />
    </button>
  );
}

function DoubleThumbsOption({ active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Love this"
      title="Love this"
      className={cn(
        'flex size-9 items-center justify-center rounded-full text-white/80 transition-all duration-200 hover:scale-110 hover:bg-white/15 hover:text-white',
        active && 'bg-primary/20 text-primary'
      )}
    >
      <span className="relative flex items-center">
        <Icon icon="si:thumb-up-alt-1-duotone" className="size-4" />
        <Icon icon="si:thumb-up-alt-1-duotone" className="-ml-1.5 size-4" />
      </span>
    </button>
  );
}

export default RatingButton;
